<template>
  <div class="page">
    <!-- ── 顶部栏 ── -->
    <header class="topbar">
      <div class="topbar__left">
        <div class="topbar__logo">
          <el-icon><Folder /></el-icon>
        </div>
        <div>
          <h1 class="topbar__title">归档管理</h1>
          <p class="topbar__sub">
            数据库只留最近 N 个完整月 · 更早的整月导出 Parquet 永久保存 ·
            核对一致才删库
          </p>
        </div>
      </div>
      <div class="topbar__right">
        <div class="runstate" :class="{ 'runstate--live': status?.running }">
          <span class="runstate__dot"></span>
          <span class="runstate__text">{{
            status?.running
              ? "任务执行中 · 5s 自动刷新"
              : "空闲 · 每月 1 号 02:00 自动执行"
          }}</span>
        </div>
        <button
          class="runbtn"
          :class="{ 'runbtn--live': status?.running }"
          @click="runNow"
        >
          <el-icon><VideoPlay /></el-icon>
          {{ status?.running ? "执行中…" : "立即执行" }}
        </button>
      </div>
    </header>

    <!-- ── 数据流水线 ── -->
    <section class="panel flow">
      <div class="panel__head">
        <span class="panel__title">数据流水线</span>
        <span class="panel__desc"
          >数据从上报到永久保存的完整去向（随运行参数实时变化）</span
        >
      </div>
      <div class="flow__track">
        <template v-for="(step, i) in pipelineSteps" :key="step.key">
          <div
            class="step"
            :class="step.on ? `step--on-${step.tone}` : 'step--off'"
          >
            <div class="step__icon">
              <el-icon><component :is="step.icon" /></el-icon>
            </div>
            <div class="step__text">
              <div class="step__name">{{ step.name }}</div>
              <div class="step__desc">{{ step.desc }}</div>
            </div>
          </div>
          <div v-if="i < pipelineSteps.length - 1" class="flow__line"></div>
        </template>
      </div>
    </section>

    <!-- ── 指标 ── -->
    <section class="metrics">
      <div class="metric">
        <div class="metric__icon metric__icon--green">
          <el-icon><Folder /></el-icon>
        </div>
        <div class="metric__body">
          <div class="metric__value">{{ statTotalSize }}</div>
          <div class="metric__label">归档文件总量</div>
        </div>
        <div class="metric__foot">永久保存 · 可查询可恢复</div>
      </div>
      <div class="metric">
        <div class="metric__icon metric__icon--blue">
          <el-icon><Tickets /></el-icon>
        </div>
        <div class="metric__body">
          <div class="metric__value">{{ statArchivedMonths }}</div>
          <div class="metric__label">已归档月份</div>
        </div>
        <div class="metric__foot">整月完成导出并删库</div>
      </div>
      <div class="metric">
        <div class="metric__icon metric__icon--amber">
          <el-icon><Timer /></el-icon>
        </div>
        <div class="metric__body">
          <div class="metric__value">{{ statPendingSources }}</div>
          <div class="metric__label">待删除源</div>
        </div>
        <div class="metric__foot">
          {{ status?.deleteEnabled ? "下次任务将删除" : "删除开关未开启" }}
        </div>
      </div>
      <div class="metric">
        <div class="metric__icon metric__icon--violet">
          <el-icon><Clock /></el-icon>
        </div>
        <div class="metric__body">
          <div class="metric__value">{{ status?.retentionMonths ?? "-" }}</div>
          <div class="metric__label">保留期 N（月）</div>
        </div>
        <div class="metric__foot">更早的月份整月归档</div>
      </div>
    </section>

    <div class="cols">
      <!-- ── 运行参数 ── -->
      <section class="panel">
        <div class="panel__head">
          <span class="panel__title">运行参数</span>
          <span class="panel__desc"
            >修改立即生效 · 灰度路径：先导出 → 再查询 → 最后删除</span
          >
        </div>
        <div class="params">
          <div
            v-for="p in paramItems"
            :key="p.key"
            class="param"
            :class="`param--${p.tone}`"
          >
            <div class="param__top">
              <div class="param__icon" :class="`param__icon--${p.tone}`">
                <el-icon><component :is="p.icon" /></el-icon>
              </div>
              <div class="param__title">
                {{ p.name }}
                <span v-if="p.tag" class="param__tag">{{ p.tag }}</span>
              </div>
              <span
                v-if="!p.chips"
                class="param__state"
                :class="
                  p.on ? `param__state--on-${p.tone}` : 'param__state--off'
                "
              >
                {{ p.on ? "已开启" : "已关闭" }}
              </span>
            </div>
            <div class="param__hint">{{ p.hint }}</div>
            <div class="param__ctrl">
              <template v-if="p.chips">
                <button
                  v-for="n in p.chips"
                  :key="n"
                  class="chip"
                  :class="{ 'chip--on': p.on === n }"
                  @click="p.toggle(n)"
                >
                  {{ n }} 月
                </button>
              </template>
              <el-switch
                v-else
                :model-value="!!p.on"
                @change="(v: any) => p.toggle(v)"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- ── 数据恢复 ── -->
      <section class="panel">
        <div class="panel__head">
          <span class="panel__title">数据恢复</span>
          <span class="badge badge--violet"
            >{{ restoreOptions.length }} 个源可恢复</span
          >
        </div>
        <p class="panel__note">
          把已删除的归档月从文件写回数据库（临时回库核对用）。删除开关开启时，下次归档任务会再次删除该月；<b>文件永久保留</b>，可反复恢复。
        </p>

        <el-select
          v-model="restoreSelected"
          class="restore__select"
          placeholder="选择：库 · 表/集合 · 月份 · 行数"
          :disabled="restoring"
        >
          <el-option
            v-for="r in restoreOptions"
            :key="r.id"
            :value="`${r.tableName}|${r.month?.slice(0, 7)}`"
            :label="`【${restoreDbLabel(r.tableName)}】${
              r.tableName
            } — ${r.month?.slice(0, 7)}（${r.rowCount.toLocaleString()} 行）`"
          />
        </el-select>

        <div class="restore__actions">
          <el-button
            type="primary"
            :disabled="restoring || !restoreSelected"
            :loading="restoring"
            @click="startRestore"
          >
            <el-icon><RefreshLeft /></el-icon>
            {{ restoring ? "恢复中…" : "开始恢复" }}
          </el-button>
          <el-button text :disabled="restoring" @click="loadRestoreOptions">
            刷新清单
          </el-button>
        </div>

        <div v-if="restoreState?.error" class="notice notice--bad">
          <el-icon><WarningFilled /></el-icon>
          <div>恢复失败：{{ restoreState.error }}</div>
        </div>
        <div v-else-if="restoreState?.tableName" class="notice notice--ok">
          <el-icon><CircleCheckFilled /></el-icon>
          <div>
            {{ restoreState.tableName }} · {{ restoreState.month }} ·
            {{ restoreState.message }}
          </div>
        </div>
        <div v-else-if="!restoreOptions.length" class="notice">
          <el-icon><InfoFilled /></el-icon>
          <div>暂无可恢复数据（没有已删除状态的源月）</div>
        </div>
      </section>
    </div>

    <!-- ── 台账 ── -->
    <section class="panel">
      <el-tabs v-model="activeTab" class="tabs">
        <el-tab-pane name="jobs">
          <template #label>
            <span class="tab-label"
              ><el-icon><Document /></el-icon>执行记录</span
            >
          </template>

          <div class="list">
            <div
              v-for="job in status?.recentJobs ?? []"
              :key="job.id"
              class="job"
              :class="`job--${job.status.toLowerCase()}`"
            >
              <div class="job__head">
                <span class="pill" :class="'pill--' + job.status.toLowerCase()">
                  {{ statusText(job.status) }}
                </span>
                <span class="job__month">{{
                  job.targetMonth?.slice(0, 7)
                }}</span>
                <span class="job__trigger">
                  {{
                    job.triggerType === "MANUAL"
                      ? "手动触发"
                      : job.triggerType === "RESTORE"
                      ? "数据恢复"
                      : "定时触发"
                  }}
                </span>
                <span class="job__time">
                  {{ job.startTime?.replace("T", " ").slice(0, 19) }}
                  <template v-if="job.endTime">
                    → {{ job.endTime?.replace("T", " ").slice(11, 19) }}
                  </template>
                </span>
              </div>
              <div class="job__body">
                <span class="job__meta">
                  源 {{ job.okSources }}/{{ job.totalSources }}
                </span>
                <span class="job__meta"
                  >{{ job.rowCount.toLocaleString() }} 行</span
                >
                <span class="job__meta">{{ formatSize(job.fileBytes) }}</span>
                <span
                  v-if="job.errorSummary"
                  class="job__error"
                  :title="job.errorSummary"
                >
                  {{ job.errorSummary }}
                </span>
              </div>
            </div>

            <div v-if="!status?.recentJobs?.length" class="empty">
              <el-icon><Document /></el-icon>
              <div>暂无执行记录，点右上角「立即执行」开始</div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane name="registry">
          <template #label>
            <span class="tab-label"
              ><el-icon><Files /></el-icon>登记明细</span
            >
          </template>

          <div class="toolbar">
            <el-date-picker
              v-model="registryMonth"
              type="month"
              value-format="YYYY-MM"
              placeholder="全部月份"
              clearable
              size="small"
              style="width: 150px"
            />
            <el-button size="small" @click="loadRegistry">查询</el-button>
            <span class="toolbar__hint">每源每月一行 · 归档数据的权威台账</span>
          </div>

          <div v-if="registryRows.length" class="tablewrap">
            <table class="reg">
              <thead>
                <tr>
                  <th>月份</th>
                  <th>源表 / 船集合</th>
                  <th class="num">行数</th>
                  <th class="num">文件大小</th>
                  <th>月批次</th>
                  <th>源状态</th>
                  <th>归档目录</th>
                  <th>导出 / 删除时间</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in registryRows" :key="row.id">
                  <td>
                    <span
                      class="monthchip"
                      :class="'monthchip--' + monthChip(row.monthState)"
                    >
                      {{ row.month?.slice(0, 7) }}
                    </span>
                  </td>
                  <td class="mono">{{ row.tableName }}</td>
                  <td class="num">{{ row.rowCount.toLocaleString() }}</td>
                  <td class="num">{{ formatSize(row.fileSize) }}</td>
                  <td>
                    <span
                      class="pill pill--plain"
                      :class="'pill--' + monthPill(row.monthState)"
                    >
                      {{ monthStateText(row.monthState) }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="pill pill--plain"
                      :class="'pill--' + sourcePill(row.sourceState)"
                    >
                      {{ sourceStateText(row.sourceState) }}
                    </span>
                  </td>
                  <td class="mono dim ellipsis" :title="row.filePath">
                    {{ row.filePath || "-" }}
                  </td>
                  <td class="dim tcell">
                    {{ row.exportTime?.replace("T", " ").slice(0, 19) }}
                    <template v-if="row.archiveTime">
                      <br />{{
                        row.archiveTime?.replace("T", " ").slice(0, 19)
                      }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else class="empty">
            <el-icon><Files /></el-icon>
            <div>暂无登记记录，先执行一次归档任务</div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  Calendar,
  CircleCheck,
  CircleCheckFilled,
  Clock,
  Cpu,
  Delete,
  Document,
  Download,
  Files,
  Folder,
  InfoFilled,
  RefreshLeft,
  Search,
  Tickets,
  Timer,
  VideoPlay,
  WarningFilled,
} from "@element-plus/icons-vue";
import {
  getArchiveStatusApi,
  runArchiveApi,
  getArchiveRegistryApi,
  updateArchiveConfigApi,
  getArchiveRestoreOptionsApi,
  startArchiveRestoreApi,
  getArchiveRestoreStatusApi,
  type ArchiveRegistryRow,
  type ArchiveStatus,
  type RestoreState,
} from "@/api/archiveApi";

// ===== 状态 =====
const status = ref<ArchiveStatus | null>(null);
const registryRows = ref<ArchiveRegistryRow[]>([]);
const registryMonth = ref<string>("");
const running = ref(false);
const activeTab = ref("jobs");

// ===== 数据恢复状态 =====
const restoreOptions = ref<ArchiveRegistryRow[]>([]);
const restoreSelected = ref<string>("");
const restoreState = ref<RestoreState | null>(null);
const restoring = ref(false);
let restorePollTimer: number | undefined;

// ===== 轮询（任务运行时 5s，空闲 30s）=====
let pollTimer: number | undefined;

const refreshStatus = async () => {
  status.value = await getArchiveStatusApi();
};

const startPolling = () => {
  stopPolling();
  const tick = async () => {
    await refreshStatus();
    pollTimer = window.setTimeout(tick, status.value?.running ? 5000 : 30000);
  };
  pollTimer = window.setTimeout(tick, 1000);
};

const stopPolling = () => {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = undefined;
  }
};

onMounted(() => {
  startPolling();
  loadRegistry();
  loadRestoreOptions();
  refreshRestoreStatus();
});
onUnmounted(() => {
  stopPolling();
  stopRestorePolling();
});

// ===== 数据流水线（随四个运行参数实时点亮）=====
const pipelineSteps = computed(() => {
  const exportOn = !!status.value?.exportEnabled;
  const deleteOn = !!status.value?.deleteEnabled;
  return [
    {
      key: "ingest",
      name: "终端上报",
      desc: "1 秒/条照常入库",
      on: true,
      tone: "blue",
      icon: Cpu,
    },
    {
      key: "export",
      name: "导出 Parquet",
      desc: exportOn ? "已开启 · 整月导出" : "未开启 · 只留库内",
      on: exportOn,
      tone: "blue",
      icon: Download,
    },
    {
      key: "verify",
      name: "行数核对",
      desc: exportOn ? "一致才继续" : "随导出进行",
      on: exportOn,
      tone: "blue",
      icon: CircleCheck,
    },
    {
      key: "delete",
      name: "删除库内明细",
      desc: deleteOn ? "已开启 · 核对后删库" : "未开启 · 库内留存",
      on: deleteOn,
      tone: "red",
      icon: Delete,
    },
    {
      key: "file",
      name: "文件永久保留",
      desc: "永不删除 · 可查询可恢复",
      on: true,
      tone: "green",
      icon: Folder,
    },
  ];
});

// ===== 运行参数（卡片化渲染）=====
const paramItems = computed(() => [
  {
    key: "export",
    name: "归档导出",
    icon: Download,
    tone: "blue",
    hint: "关闭后任务完全不动，数据只留库内（安全方向）",
    on: !!status.value?.exportEnabled,
    toggle: (v: string | number | boolean) =>
      updateConfig("archive.export.enabled", String(v)),
  },
  {
    key: "delete",
    name: "归档删除",
    icon: Delete,
    tone: "red",
    tag: "高危",
    hint: "仅在「导出 + 核对 + 登记」全部成功后才会删库数据",
    on: !!status.value?.deleteEnabled,
    toggle: (v: string | number | boolean) =>
      updateConfig("archive.delete.enabled", String(v)),
  },
  {
    key: "query",
    name: "归档查询",
    icon: Search,
    tone: "violet",
    hint: "开：归档月查文件（5 分钟粒度）；关：全部查数据库",
    on: !!status.value?.queryEnabled,
    toggle: (v: string | number | boolean) =>
      updateConfig("archive.query.enabled", String(v)),
  },
  {
    key: "retention",
    name: "保留期 N",
    icon: Calendar,
    tone: "green",
    hint: "保留最近 N 个完整自然月 + 当月，更早的整月归档",
    on: status.value?.retentionMonths ?? 1,
    chips: [1, 3, 6, 12],
    toggle: (n: number) => updateConfig("archive.retention.months", String(n)),
  },
]);

// ===== 指标（从登记台账聚合）=====
const statArchivedMonths = computed(
  () =>
    new Set(
      registryRows.value
        .filter((r) => r.monthState === "ARCHIVED")
        .map((r) => r.month)
    ).size
);
const statTotalSize = computed(() =>
  formatSize(registryRows.value.reduce((s, r) => s + (r.fileSize ?? 0), 0))
);
/** 待删除源数（已导出未删除）；删除开关开启时会被下次任务删掉 */
const statPendingSources = computed(
  () => registryRows.value.filter((r) => r.sourceState === "EXPORTED").length
);

// ===== 数据恢复 =====
const loadRestoreOptions = async () => {
  restoreOptions.value = (await getArchiveRestoreOptionsApi()) ?? [];
};

const refreshRestoreStatus = async () => {
  const st = await getArchiveRestoreStatusApi();
  if (st) {
    restoreState.value = st;
    restoring.value = st.running;
    if (!st.running) {
      stopRestorePolling();
      if (st.tableName) {
        loadRegistry();
        loadRestoreOptions();
        refreshStatus();
      }
    }
  }
};

const startRestorePolling = () => {
  stopRestorePolling();
  const tick = async () => {
    await refreshRestoreStatus();
    if (restoring.value) {
      restorePollTimer = window.setTimeout(tick, 3000);
    }
  };
  restorePollTimer = window.setTimeout(tick, 2000);
};

const stopRestorePolling = () => {
  if (restorePollTimer) {
    clearTimeout(restorePollTimer);
    restorePollTimer = undefined;
  }
};

const restoreDbLabel = (tableName: string) =>
  tableName.startsWith("deviceReport_") ? "MongoDB" : "ClickHouse";

const startRestore = async () => {
  const row = restoreOptions.value.find(
    (r) => `${r.tableName}|${r.month?.slice(0, 7)}` === restoreSelected.value
  );
  if (!row || !row.month) {
    ElMessage.warning("请先选择要恢复的源和月份");
    return;
  }
  const month = row.month.slice(0, 7);
  const deleteOn = status.value?.deleteEnabled;
  const queryOn = status.value?.queryEnabled;
  try {
    await ElMessageBox.confirm(
      `将把【${restoreDbLabel(row.tableName)}】${
        row.tableName
      } 的 ${month} 数据` +
        `（${row.rowCount.toLocaleString()} 行）从归档文件写回数据库。\n\n` +
        (deleteOn
          ? "⚠️ 当前删除开关为【开启】：下次归档任务执行时（每月 1 号自动或手动触发）会再次删除该月数据（文件永久保留）。如需数据留库使用一段时间，请先关闭删除开关。\n\n"
          : "当前删除开关为【关闭】：数据会一直留库；待开启删除开关后，下次归档任务会将该月数据再次删除（文件永久保留）。\n\n") +
        (queryOn
          ? "📌 当前查询路由开关为【开启】：用户端查该月曲线仍走归档文件（5 分钟粒度）。要在页面查看恢复的逐秒明细，请关闭「归档查询」开关。"
          : "当前查询路由开关为【关闭】：页面查询全部走数据库，可直接查看恢复的逐秒明细。"),
      "高危操作确认",
      {
        type: "warning",
        confirmButtonText: "我已知晓，确认恢复",
        cancelButtonText: "取消",
      }
    );
  } catch {
    return; // 用户取消
  }
  const res = await startArchiveRestoreApi(row.tableName, month);
  if (res?.started) {
    ElMessage.success("恢复已启动，正在执行…");
    restoring.value = true;
    startRestorePolling();
  } else {
    ElMessage.error(res?.error || "启动失败");
  }
};

// ===== 操作 =====
const runNow = async () => {
  if (status.value?.running) {
    ElMessage.info("任务正在运行中，页面每 5 秒自动刷新");
    return;
  }
  running.value = true;
  try {
    const ok = await runArchiveApi();
    if (ok) {
      ElMessage.success("已触发");
      setTimeout(refreshStatus, 1000);
    } else {
      ElMessage.warning("触发失败，请查看后端日志");
    }
  } finally {
    running.value = false;
  }
};

const updateConfig = async (key: string, value: string) => {
  if (key === "archive.delete.enabled" && value === "true") {
    ElMessage.warning("删除开关已开启：请确认灰度验证（文件抽查）已完成");
  }
  const ok = await updateArchiveConfigApi(key, value);
  if (ok) {
    ElMessage.success("参数已生效");
    refreshStatus();
  } else {
    ElMessage.error("修改失败");
  }
};

const loadRegistry = async () => {
  registryRows.value =
    (await getArchiveRegistryApi(registryMonth.value || undefined)) ?? [];
};

// ===== 格式化 / 状态映射 =====
const formatSize = (bytes: number): string => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const statusText = (s: string) =>
  ({ RUNNING: "运行中", SUCCESS: "成功", PARTIAL: "部分失败", FAILED: "失败" }[
    s
  ] ?? s);

const monthStateText = (s: string) =>
  ({
    EXPORTING: "导出中",
    ALL_EXPORTED: "待删除",
    ARCHIVED: "已归档",
    FAILED: "失败",
    PARTIAL: "部分失败",
  }[s] ?? s);
const monthPill = (s: string) =>
  ({
    EXPORTING: "warning",
    ALL_EXPORTED: "blue",
    ARCHIVED: "success",
    FAILED: "danger",
    PARTIAL: "warning",
  }[s] ?? "gray");

const sourceStateText = (s: string) =>
  ({ PENDING: "未处理", EXPORTED: "已导出", ARCHIVED: "已删除" }[s] ?? s);
const sourcePill = (s: string) =>
  ({ PENDING: "gray", EXPORTED: "amber", ARCHIVED: "success" }[s] ?? "gray");

/** 登记明细月份 chip 配色（与月批次状态对应） */
const monthChip = (s: string) =>
  ({
    EXPORTING: "pending",
    ALL_EXPORTED: "waiting",
    ARCHIVED: "done",
    FAILED: "bad",
    PARTIAL: "waiting",
  }[s] ?? "idle");
</script>

<style lang="less" scoped>
// ===== 设计变量 =====
@bg: #f4f6fa;
@card: #ffffff;
@ink: #0f172a;
@ink-2: #475569;
@ink-3: #94a3b8;
@line: #e8edf4;
@blue: #2563eb;
@blue-bg: #eff6ff;
@green: #059669;
@green-bg: #ecfdf5;
@amber: #b45309;
@amber-bg: #fffbeb;
@red: #dc2626;
@red-bg: #fef2f2;
@violet: #7c3aed;
@violet-bg: #f5f3ff;
@radius: 16px;
@shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
@shadow-hover: 0 12px 32px rgba(15, 23, 42, 0.08);

.page {
  min-height: 100%;
  padding: 24px 28px 44px;
  box-sizing: border-box;
  background: @bg;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

// ===== 顶部栏 =====
.topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding: 20px 24px;
  background: @card;
  border: 1px solid @line;
  border-radius: @radius;
  box-shadow: @shadow;

  &__left {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
  }
  &__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 13px;
    font-size: 22px;
    color: #fff;
    background: linear-gradient(135deg, #3b82f6 0%, #7c3aed 100%);
    box-shadow: 0 8px 18px rgba(59, 130, 246, 0.28);
    flex-shrink: 0;
  }
  &__title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: @ink;
    letter-spacing: 0.3px;
  }
  &__sub {
    margin: 4px 0 0;
    font-size: 12px;
    color: @ink-3;
  }
  &__right {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
  }
}

.runstate {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  font-size: 12px;
  color: @ink-2;
  background: #f8fafc;
  border: 1px solid @line;
  border-radius: 999px;

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #cbd5e1;
  }
  &--live {
    color: @green;
    background: @green-bg;
    border-color: rgba(5, 150, 105, 0.2);
    .runstate__dot {
      background: @green;
      animation: pulse 1.6s ease-in-out infinite;
    }
  }
}
@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.35);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(5, 150, 105, 0);
  }
}

.runbtn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: @ink;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1e293b;
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(15, 23, 42, 0.22);
  }
  &--live {
    background: @green;
    cursor: default;
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
}

// ===== 通用面板 =====
.panel {
  background: @card;
  border: 1px solid @line;
  border-radius: @radius;
  box-shadow: @shadow;
  padding: 20px 22px;

  &__head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  &__title {
    font-size: 15px;
    font-weight: 700;
    color: @ink;
  }
  &__desc {
    font-size: 12px;
    color: @ink-3;
  }
  &__note {
    margin: -6px 0 14px;
    font-size: 12px;
    line-height: 1.7;
    color: @ink-3;
    b {
      color: @ink-2;
    }
  }
}

// ===== 数据流水线 =====
.flow__track {
  display: flex;
  align-items: center;
}
.flow__line {
  flex: 1;
  min-width: 18px;
  height: 1px;
  margin: 0 6px;
  background: linear-gradient(90deg, #dbe3ee, #e8edf4);
}
.step {
  display: flex;
  align-items: center;
  gap: 11px;
  flex-shrink: 0;

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    font-size: 17px;
    color: @ink-3;
    background: #f1f5f9;
    transition: all 0.25s ease;
  }
  &__name {
    font-size: 13px;
    font-weight: 700;
    color: @ink-3;
    white-space: nowrap;
  }
  &__desc {
    margin-top: 2px;
    font-size: 11px;
    color: @ink-3;
    white-space: nowrap;
  }

  &--on-blue .step__icon {
    color: @blue;
    background: @blue-bg;
  }
  &--on-blue .step__name {
    color: @ink;
  }
  &--on-blue .step__desc {
    color: @blue;
  }
  &--on-green .step__icon {
    color: @green;
    background: @green-bg;
  }
  &--on-green .step__name {
    color: @ink;
  }
  &--on-green .step__desc {
    color: @green;
  }
  &--on-red .step__icon {
    color: @red;
    background: @red-bg;
  }
  &--on-red .step__name {
    color: @ink;
  }
  &--on-red .step__desc {
    color: @red;
  }
}

// ===== 指标卡 =====
.metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.metric {
  position: relative;
  padding: 18px 20px 16px;
  background: @card;
  border: 1px solid @line;
  border-radius: @radius;
  box-shadow: @shadow;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: @shadow-hover;
  }
  &__icon {
    position: absolute;
    top: 16px;
    right: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    font-size: 17px;

    &--blue {
      color: @blue;
      background: @blue-bg;
    }
    &--green {
      color: @green;
      background: @green-bg;
    }
    &--amber {
      color: @amber;
      background: @amber-bg;
    }
    &--violet {
      color: @violet;
      background: @violet-bg;
    }
  }
  &__value {
    font-size: 24px;
    font-weight: 700;
    color: @ink;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }
  &__label {
    margin-top: 3px;
    font-size: 12px;
    font-weight: 600;
    color: @ink-2;
  }
  &__foot {
    margin-top: 8px;
    font-size: 11px;
    color: @ink-3;
  }
}

// ===== 两列布局 =====
.cols {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 18px;
  align-items: start;
}

// ===== 参数卡（2×2 网格）=====
.params {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
.param {
  padding: 16px 18px;
  border: 1px solid @line;
  border-radius: 14px;
  background: #fcfdfe;
  transition: all 0.2s ease;

  &:hover {
    background: #fff;
    border-color: #dbe7f5;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
  }
  &__top {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    font-size: 15px;

    &--blue {
      color: @blue;
      background: @blue-bg;
    }
    &--red {
      color: @red;
      background: @red-bg;
    }
    &--violet {
      color: @violet;
      background: @violet-bg;
    }
    &--green {
      color: @green;
      background: @green-bg;
    }
  }
  &__title {
    font-size: 14px;
    font-weight: 700;
    color: @ink;
  }
  &__tag {
    margin-left: 6px;
    padding: 1px 7px;
    font-size: 10px;
    font-weight: 700;
    color: @red;
    background: @red-bg;
    border-radius: 999px;
  }
  &__state {
    margin-left: auto;
    font-size: 12px;
    font-weight: 700;

    &--off {
      color: @ink-3;
    }
    &--on-blue {
      color: @blue;
    }
    &--on-red {
      color: @red;
    }
    &--on-violet {
      color: @violet;
    }
  }
  &__hint {
    margin-top: 10px;
    font-size: 12px;
    line-height: 1.6;
    color: @ink-3;
  }
  &__ctrl {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-top: 12px;
  }
}

.chip {
  padding: 6px 14px;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 600;
  color: @ink-2;
  background: #f1f5f9;
  border: 1px solid transparent;
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.18s ease;

  &:first-child {
    margin-left: 0;
  }
  &:hover {
    background: #e6ecf3;
  }
  &--on {
    color: #fff;
    background: @green;
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.28);
  }
}

// ===== 数据恢复 =====
.restore__select {
  width: 100%;
}
.restore__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}
.notice {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin-top: 16px;
  padding: 11px 14px;
  font-size: 12px;
  line-height: 1.6;
  color: @ink-2;
  background: #f8fafc;
  border: 1px solid @line;
  border-radius: 11px;

  .el-icon {
    margin-top: 2px;
    color: @ink-3;
  }
  &--ok {
    color: @green;
    background: @green-bg;
    border-color: rgba(5, 150, 105, 0.18);
    .el-icon {
      color: @green;
    }
  }
  &--bad {
    color: @red;
    background: @red-bg;
    border-color: rgba(220, 38, 38, 0.18);
    .el-icon {
      color: @red;
    }
  }
}

// ===== Tabs 与记录 =====
.tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 14px;
  }
  :deep(.el-tabs__item) {
    font-weight: 700;
  }
  :deep(.el-tabs__nav-wrap::after) {
    background: @line;
  }
}
.tab-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.job {
  position: relative;
  padding: 14px 16px 14px 18px;
  border: 1px solid @line;
  border-radius: 13px;
  background: #fff;
  overflow: hidden;
  transition: all 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: #e2e8f0;
  }
  &:hover {
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.05);
  }
  &--success::before {
    background: @green;
  }
  &--running::before {
    background: @blue;
  }
  &--partial::before {
    background: @amber;
  }
  &--failed::before {
    background: @red;
  }
  &--partial {
    background: @amber-bg;
    border-color: rgba(180, 83, 9, 0.15);
  }
  &--failed {
    background: @red-bg;
    border-color: rgba(220, 38, 38, 0.15);
  }
  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__month {
    font-size: 14px;
    font-weight: 700;
    color: @ink;
    font-variant-numeric: tabular-nums;
  }
  &__trigger {
    padding: 2px 9px;
    font-size: 11px;
    color: @ink-2;
    background: #f1f5f9;
    border-radius: 6px;
  }
  &__time {
    margin-left: auto;
    font-size: 12px;
    color: @ink-3;
    font-variant-numeric: tabular-nums;
  }
  &__body {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 9px;
  }
  &__meta {
    font-size: 12px;
    color: @ink-2;
    font-variant-numeric: tabular-nums;
  }
  &__error {
    font-size: 12px;
    color: @red;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 420px;
  }
}

// ===== 登记明细表 =====
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  &__hint {
    margin-left: auto;
    font-size: 12px;
    color: @ink-3;
  }
}
.tablewrap {
  overflow-x: auto;
  border: 1px solid @line;
  border-radius: 12px;
}
.reg {
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 11px 12px;
    font-size: 11px;
    font-weight: 700;
    color: @ink-3;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    text-align: left;
    white-space: nowrap;
    background: #f8fafc;
    border-bottom: 1px solid @line;
  }
  td {
    padding: 11px 12px;
    font-size: 13px;
    color: @ink-2;
    border-bottom: 1px solid @line;
    vertical-align: middle;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }
  tbody tr {
    transition: background 0.15s ease;
    &:hover {
      background: #fafbfd;
    }
  }
  .num {
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .mono {
    font-family: "JetBrains Mono", Consolas, monospace;
    font-size: 12px;
  }
  .dim {
    color: @ink-3;
  }
  .ellipsis {
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .tcell {
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    line-height: 1.5;
  }
}

// ===== 胶囊 / 徽标 / 空状态 =====
.pill {
  display: inline-block;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  white-space: nowrap;

  &--success {
    color: @green;
    background: @green-bg;
  }
  &--running {
    color: @blue;
    background: @blue-bg;
  }
  &--blue {
    color: @blue;
    background: @blue-bg;
  }
  &--warning,
  &--partial {
    color: @amber;
    background: @amber-bg;
  }
  &--amber {
    color: @amber;
    background: @amber-bg;
  }
  &--danger,
  &--failed {
    color: @red;
    background: @red-bg;
  }
  &--gray {
    color: @ink-3;
    background: #f1f5f9;
  }
  &--plain {
    font-weight: 600;
  }
}
.monthchip {
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
  color: @ink-2;
  background: #f1f5f9;

  &--done {
    color: @green;
    background: @green-bg;
  }
  &--waiting {
    color: @blue;
    background: @blue-bg;
  }
  &--pending {
    color: @amber;
    background: @amber-bg;
  }
  &--bad {
    color: @red;
    background: @red-bg;
  }
}
.badge {
  margin-left: auto;
  padding: 3px 11px;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;

  &--violet {
    color: @violet;
    background: @violet-bg;
  }
}
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 46px 0;
  font-size: 13px;
  color: @ink-3;

  .el-icon {
    font-size: 30px;
    color: #cbd5e1;
  }
}

// ===== 响应式 =====
@media (max-width: 1280px) {
  .metrics {
    grid-template-columns: repeat(2, 1fr);
  }
  .cols {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 860px) {
  .params {
    grid-template-columns: 1fr;
  }
  .flow__track {
    flex-wrap: wrap;
    gap: 12px;
  }
  .flow__line {
    display: none;
  }
}
</style>
